import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Trial } from '../types/trial.types'
import { trialsService } from '../services/trials.service'

export const useTrials = () => {
    const [searchText, setSearchText] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(15)

    const { data: trialsData, isLoading, refetch } = useQuery({
        queryKey: ['trials', 'list', page, pageSize, statusFilter],
        queryFn: async () => {
            // 1. Fetch Trials
            const response = await trialsService.getTrials(page, pageSize, statusFilter)
            const backendTrials = response.data

            // 2. Map to UI Model (Merchant details are now embedded in response)
            const mappedTrials: Trial[] = backendTrials.map(bt => {
                const merchant = bt.merchant
                
                // Calculate remaining days if not provided
                let remaining = bt.days_remaining
                if (remaining === undefined && bt.status === 'active') {
                   const end = new Date(bt.trial_end_at)
                   const now = new Date()
                   const diffTime = Math.abs(end.getTime() - now.getTime())
                   remaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) 
                   if (end < now) remaining = 0
                }

                return {
                    id: bt.id.toString(),
                    merchantName: merchant?.business_name || `Merchant #${bt.merchant_id}`,
                    merchantEmail: merchant?.email || 'N/A',
                    merchantId: bt.merchant_id,
                    duration: bt.trial_duration_days,
                    startDate: bt.trial_start_at,
                    endDate: bt.trial_end_at,
                    status: bt.status,
                    assignedBy: 'Admin', // Placeholder
                    reason: bt.metadata?.notes,
                    remainingDays: remaining
                } as Trial
            })

            return {
                trials: mappedTrials,
                total: response.total
            }
        },
        placeholderData: (previousData) => previousData,
    })

    // Client-side filtering for search (since backend search isn't implemented yet)
    const filteredTrials = useMemo(() => {
        if (!trialsData?.trials) return []
        
        return trialsData.trials.filter(trial => {
            return trial.merchantName.toLowerCase().includes(searchText.toLowerCase()) || 
                   trial.merchantEmail.toLowerCase().includes(searchText.toLowerCase())
        })
    }, [trialsData, searchText])

    return {
        trials: filteredTrials,
        searchText,
        setSearchText,
        statusFilter,
        setStatusFilter,
        totalTrials: trialsData?.total || 0,
        activeTrials: trialsData?.trials.filter(t => t.status === 'active').length || 0,
        isLoading,
        refetch,
        page,
        setPage,
        pageSize,
        setPageSize
    }
}
